<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { CornerDownLeft, Search, X } from '@lucide/vue'
import type { AdminLayoutMenuGroup, AdminLayoutMenuItem } from '../../types'

const props = defineProps<{
  open: boolean
  groups: AdminLayoutMenuGroup[]
}>()

const emit = defineEmits<{
  close: []
  select: [item: AdminLayoutMenuItem, group: AdminLayoutMenuGroup]
}>()

const query = ref('')
const activeIndex = ref(0)
const input = ref<HTMLInputElement | null>(null)

const commands = computed(() => {
  const normalized = query.value.trim().toLocaleLowerCase()
  return props.groups.flatMap((group) => group.items
    .filter((item, index, items) => {
      const next = items[index + 1]
      return !item.disabled && (!next || (next.depth ?? 0) <= (item.depth ?? 0))
    })
    .map((item) => ({ group, item })))
    .filter(({ group, item }) => !normalized || `${group.label} ${item.label} ${item.subtitle ?? ''}`.toLocaleLowerCase().includes(normalized))
    .slice(0, 18)
})

watch(() => props.open, async (open) => {
  if (!open) return
  query.value = ''
  activeIndex.value = 0
  await nextTick()
  input.value?.focus()
})

watch(commands, () => {
  activeIndex.value = commands.value.length
    ? Math.max(0, Math.min(activeIndex.value, commands.value.length - 1))
    : 0
})

function selectCommand(index: number) {
  const command = commands.value[index]
  if (!command) return
  emit('select', command.item, command.group)
  emit('close')
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close')
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!commands.value.length) return
    activeIndex.value = Math.min(activeIndex.value + 1, commands.value.length - 1)
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (!commands.value.length) return
    activeIndex.value = Math.max(activeIndex.value - 1, 0)
  }
  if (event.key === 'Enter') {
    event.preventDefault()
    selectCommand(activeIndex.value)
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="rr-command-backdrop" @mousedown.self="emit('close')">
      <section class="rr-command" role="dialog" aria-modal="true" aria-label="快速导航" @keydown="handleKeydown">
        <label class="rr-command__search">
          <Search :size="18" :stroke-width="1.7" aria-hidden="true" />
          <input ref="input" v-model="query" type="search" placeholder="搜索菜单和工作区" aria-label="搜索菜单和工作区" />
          <button type="button" title="关闭" aria-label="关闭" @click="emit('close')"><X :size="16" /></button>
        </label>
        <div v-if="commands.length" class="rr-command__list" role="listbox">
          <button
            v-for="(command, index) in commands"
            :key="`${command.group.id}:${command.item.id}`"
            type="button"
            :class="{ active: index === activeIndex }"
            role="option"
            :aria-selected="index === activeIndex"
            @mouseenter="activeIndex = index"
            @click="selectCommand(index)"
          >
            <span>
              <strong>{{ command.item.label }}</strong>
              <small>{{ command.group.label }}<template v-if="command.item.subtitle"> · {{ command.item.subtitle }}</template></small>
            </span>
            <CornerDownLeft v-if="index === activeIndex" :size="15" :stroke-width="1.8" aria-hidden="true" />
          </button>
        </div>
        <div v-else class="rr-command__empty">没有匹配的菜单</div>
        <footer><span>↑↓ 选择</span><span>Enter 打开</span><span>Esc 关闭</span></footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.rr-command-backdrop {
  position: fixed;
  z-index: var(--rr-z-command);
  inset: 0;
  display: grid;
  place-items: start center;
  padding: min(16vh, 150px) 16px 16px;
  background: rgb(18 22 28 / 38%);
  backdrop-filter: blur(3px);
}

.rr-command {
  width: min(620px, 100%);
  overflow: hidden;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 8px;
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-overlay);
}

.rr-command__search {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 32px;
  align-items: center;
  gap: 8px;
  min-height: 54px;
  border-bottom: 1px solid var(--rr-color-border);
  color: var(--rr-color-text-muted);
  padding: 0 12px 0 16px;
}

.rr-command__search input {
  width: 100%;
  height: 42px;
  border: 0;
  outline: 0;
  background: transparent;
  box-shadow: none;
  color: var(--rr-color-text-strong);
  font-size: 14px;
}

.rr-command__search button {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  border: 0;
  border-radius: var(--rr-radius-control);
  background: transparent;
  color: var(--rr-color-text-muted);
  cursor: pointer;
}

.rr-command__list {
  display: grid;
  max-height: min(52vh, 460px);
  gap: 2px;
  overflow: auto;
  padding: 7px;
}

.rr-command__list button {
  display: flex;
  width: 100%;
  min-height: 48px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 0 !important;
  border-radius: var(--rr-radius-control);
  background: transparent !important;
  color: var(--rr-color-text) !important;
  cursor: pointer;
  padding: 7px 10px !important;
  margin: 0 !important;
  text-align: left;
}

.rr-command__list button.active {
  background: var(--rr-color-primary-soft) !important;
  color: var(--rr-color-primary) !important;
}

.rr-command__list button > span {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.rr-command__list strong,
.rr-command__list small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rr-command__list strong { font-size: 13px; }
.rr-command__list small { color: var(--rr-color-text-muted); font-size: 11px; }
.rr-command__empty { padding: 44px 16px; color: var(--rr-color-text-muted); text-align: center; }
.rr-command footer {
  display: flex;
  gap: 14px;
  border-top: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 10px;
  padding: 7px 12px;
}
</style>
